import * as Dialog from "@radix-ui/react-dialog";
import { Check, Monitor, Moon, Palette, Sun, X } from "lucide-react";
import type { ColorMode, Preset } from "../theme/ThemeProvider";
import { useTheme } from "../theme/ThemeProvider";

const MODE_META: { key: ColorMode; label: string; icon: typeof Sun }[] = [
    { key: "light", label: "Light", icon: Sun },
    { key: "dark", label: "Dark", icon: Moon },
    { key: "system", label: "System", icon: Monitor },
];

const PRESET_LABEL: Record<Preset, string> = {
    ede: "EDE — Ocean Blue",
    canonical: "Canonical Orange",
    graphite: "Graphite",
    arctic: "Arctic",
    emerald: "Emerald",
};

// Theme preferences as a proper Radix Dialog (not inline in the user dropdown).
export function ThemeDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (value: boolean) => void }) {
    const theme = useTheme();

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Portal>
                <Dialog.Overlay className="theme-dialog__overlay" />
                <Dialog.Content className="theme-dialog">
                    <header className="theme-dialog__head">
                        <Dialog.Title className="theme-dialog__title">
                            <Palette size={16} />
                            <span>Theme Settings</span>
                        </Dialog.Title>
                        <Dialog.Close asChild>
                            <button className="theme-dialog__close" aria-label="Close">
                                <X size={16} />
                            </button>
                        </Dialog.Close>
                    </header>
                    <Dialog.Description className="theme-dialog__sr-only">
                        Choose the color mode and palette for the workspace.
                    </Dialog.Description>

                    <section className="theme-dialog__section">
                        <h4 className="theme-dialog__section-label">Color Mode</h4>
                        <div className="theme-dialog__mode-grid">
                            {MODE_META.map((m) => {
                                const Icon = m.icon;
                                const active = theme.mode === m.key;
                                return (
                                    <button
                                        key={m.key}
                                        type="button"
                                        aria-pressed={active}
                                        className={`theme-dialog__mode ${active ? "is-active" : ""}`}
                                        onClick={() => theme.setMode(m.key)}
                                    >
                                        <Icon size={20} />
                                        <span>{m.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section className="theme-dialog__section">
                        <h4 className="theme-dialog__section-label">Palette</h4>
                        <div className="theme-dialog__preset-list">
                            {theme.presets.map((preset) => {
                                const active = theme.preset === preset;
                                return (
                                    <button
                                        key={preset}
                                        type="button"
                                        aria-pressed={active}
                                        className={`theme-dialog__preset ${active ? "is-active" : ""}`}
                                        onClick={() => theme.setPreset(preset)}
                                    >
                                        <span className="theme-dialog__preset-swatch" data-swatch={preset} />
                                        <span className="theme-dialog__preset-label">{PRESET_LABEL[preset]}</span>
                                        {active ? <Check size={14} className="theme-dialog__preset-check" /> : null}
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <footer className="theme-dialog__foot">
                        <Dialog.Close asChild>
                            <button type="button" className="btn btn--positive theme-dialog__done">
                                Done
                            </button>
                        </Dialog.Close>
                    </footer>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}
